<template>
  <div class="waw-progress">
    <div class="wst-stepper">
      <div
        v-for="(m, i) in milestones"
        :key="m.key"
        class="wst-step"
        :class="'is-' + m.state"
      >
        <div v-if="i < milestones.length - 1" class="wst-bar" />
        <div class="wst-dot">
          <template v-if="m.state === 'done'">&#x2713;</template>
          <template v-else>{{ i + 1 }}</template>
        </div>
        <div class="wst-lbl">{{ m.label }}</div>
      </div>
    </div>

    <p v-if="progressLine" class="waw-line">{{ progressLine }}</p>
  </div>
</template>

<script>
/**
 * Wizard screen — the unattended back half: register the number with WhatsApp, submit the reminder
 * message, wait for WhatsApp's approval, switch it on.
 *
 * ── THIS IS THE EXISTING STEPPER, NOT A NEW ONE ──────────────────────────────────────────────────
 * The four milestones and the single status sentence come from `milestoneStates` and `statusLine`
 * in store/whatsappSelfStart.js — the same pure functions, with the same labels, that the non-wizard
 * tab has rendered since 2026-08-18. The shell passes the computed arrays in rather than this
 * component deriving them, so there is exactly one definition of what "done" means and a practice
 * that saw the old tab sees the same four milestones in the same words.
 *
 * The markup and class names are lifted from WhatsAppTab.vue's in-progress card for the same
 * reason: it is the same stepper, and two implementations would drift the first time either is
 * restyled.
 *
 * ── WHAT THIS SCREEN MUST NEVER SHOW ─────────────────────────────────────────────────────────────
 * A snag collapses to one neutral sentence ("we hit a snag — our team is on it"). Raw Meta/Twilio
 * rejection text is operator-only, and `statusLine` takes a BOOLEAN, so there is no path for an
 * error string to reach this screen at all.
 */
export default {
  name: 'WizardProgressStep',

  props: {
    /** [{ key, label, state: done|current|pending }] from milestoneStates(). */
    milestones: { type: Array, default: () => [] },
    /** The single neutral sentence from statusLine(), or '' for nothing to say. */
    progressLine: { type: String, default: '' },
  },
};
</script>

<style lang="scss" scoped>
@import '../../PatientPayments/tokens';

/* Same stepper as WhatsAppTab's in-progress card. The one colour deliberately left hardcoded is
   #25d366: that is WhatsApp's own brand green and it identifies the channel, so it is not ours
   to retone. */
.wst-stepper {
  display: flex;
  align-items: flex-start;
  margin: 0 2px 1.25rem;
}

.wst-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
  font-size: 0.75rem;
  color: $pp-text-subtle;
  text-align: center;
}

.wst-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 700;
  background: $pp-surface;
  border: 2px solid $pp-border;
  color: $pp-text-subtle;
  z-index: 1;
}

.wst-step.is-done .wst-dot {
  background: #25d366;
  border-color: #25d366;
  color: #fff;
}

.wst-step.is-current .wst-dot {
  background: $pp-brand;
  border-color: $pp-brand;
  color: #fff;
  box-shadow: 0 0 0 4px rgba(15, 114, 89, 0.12);
}

.wst-step.is-done .wst-lbl,
.wst-step.is-current .wst-lbl {
  color: $pp-text-strong;
  font-weight: 600;
}

.wst-lbl {
  margin-top: 0.5rem;
  max-width: 110px;
  line-height: 1.4;
}

.wst-bar {
  position: absolute;
  top: 14px;
  left: 50%;
  width: 100%;
  height: 2px;
  background: $pp-border;
  z-index: 0;
}

.wst-step.is-done .wst-bar {
  background: #25d366;
}

.waw-line {
  margin: 0;
  color: $pp-text-muted;
  font-size: 0.9rem;
  line-height: 1.6;
}

/* Four labels never fit side by side on a phone. */
@media (max-width: 575.98px) {
  .wst-stepper {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .wst-step {
    flex-direction: row;
    align-items: center;
    gap: 0.6rem;
    text-align: left;
    width: 100%;
  }

  .wst-lbl {
    margin-top: 0;
    max-width: none;
  }

  .wst-bar {
    display: none;
  }
}
</style>
